import { Component } from '@theme/component';

/**
 * @typedef {object} CardSwipeRefs
 * @property {HTMLAnchorElement} imageLink
 * @property {HTMLImageElement} primaryImage
 * @property {HTMLImageElement} [bufferImage]
 * @property {HTMLImageElement} [previousImage]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLScriptElement} [imageData]
 * @property {HTMLElement} [progressFill]
 * @property {HTMLElement} [status]
 */

/** @extends {Component<CardSwipeRefs>} */
class TeylensCardSwipe extends Component {
  requiredRefs = ['imageLink', 'primaryImage'];
  #mode = window.matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)');
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #images = [];
  #panels = [];
  #index = 0;
  #generation = 0;
  #listeners;
  #observer;
  #pointer = null;
  #suppressClickUntil = 0;
  #source;
  #width = 1;
  #offset = 0;
  #frame = 0;
  #settlement;
  #pending = 0;
  #rtl = 1;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#mode.addEventListener('change', this.#reset, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('mousedown', this.#onMouseDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#cancelPointer, { signal });
    this.addEventListener('lostpointercapture', this.#cancelPointer, { signal });
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#readImages();
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#readImages() || this.#panels[0]?.image !== this.refs.primaryImage) this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#generation++;
    this.#pending++;
    this.#releasePointer();
    this.#settlement?.cancel();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#offset = 0;
    this.removeAttribute('data-dragging');
    this.#paint();
  }

  #readImages() {
    const rawData = this.refs.imageData?.textContent || '[]';
    const sourceKey = rawData + this.dataset.initialImage + this.dataset.initialSource;
    if (this.#source === sourceKey) return false;
    this.#source = sourceKey;
    try {
      const data = JSON.parse(rawData);
      this.#images = Array.isArray(data) ? data.map((image) => typeof image === 'string' ? { src: image } : image).filter((image) => {
        try {
          return ['https:', 'http:'].includes(new URL(image.src || image.url, window.location.href).protocol);
        } catch {
          return false;
        }
      }) : [];
    } catch {
      this.#images = [];
    }
    const initial = this.#images.findIndex((image) => String(image.id) === this.dataset.initialImage);
    if (initial > 0) this.#images.unshift(...this.#images.splice(initial, 1));
    const first = this.#images[0];
    if (first) {
      first.initialSrc = this.refs.primaryImage.src;
      first.initialSrcset = this.refs.primaryImage.srcset;
    }
    this.dataset.imageCount = String(Math.max(this.#images.length, 1));
    this.style.setProperty('--ty-swipe-count', String(Math.max(this.#images.length, 1)));
    return true;
  }

  #reset = () => {
    this.#generation++;
    this.#pending++;
    this.#releasePointer();
    this.#settlement?.cancel();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#observer?.disconnect();
    this.#index = 0;
    this.#offset = 0;
    this.#suppressClickUntil = 0;
    this.#width = this.getBoundingClientRect().width || 1;
    this.#rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.removeAttribute('data-dragging');
    this.refs.imageLink.style.transform = '';
    const { primaryImage, bufferImage, previousImage, nextButton } = this.refs;
    const first = this.#images[0];
    if (first?.initialSrc) {
      primaryImage.srcset = first.initialSrcset;
      primaryImage.src = first.initialSrc;
      primaryImage.alt = first.alt || this.refs.imageLink.getAttribute('aria-label') || '';
    }
    this.#panels = [
      { image: primaryImage, position: 0, index: 0, ready: true },
      { image: bufferImage, position: 1, index: -1, ready: false },
      { image: previousImage, position: -1, index: -1, ready: false },
    ].filter((panel) => panel.image);
    for (const panel of this.#panels) {
      panel.image.removeAttribute('data-ready');
      if (panel.position === 0) {
        panel.image.setAttribute('data-ready', 'true');
        panel.image.setAttribute('data-active', 'true');
        panel.image.removeAttribute('aria-hidden');
      } else {
        panel.image.removeAttribute('data-active');
        panel.image.setAttribute('aria-hidden', 'true');
      }
    }
    if (nextButton) nextButton.hidden = !this.#mode.matches || this.#images.length < 2;
    this.#updateStatus(false);
    this.#paint();
    if (!this.#mode.matches || this.#images.length < 2) return;
    if (typeof IntersectionObserver === 'undefined') {
      this.#warmAdjacent();
      return;
    }
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#warmAdjacent();
      this.#observer?.disconnect();
    }, { rootMargin: '120px' });
    this.#observer.observe(this);
  };

  #warmAdjacent() {
    if (!this.#mode.matches || this.#images.length < 2) return;
    for (const panel of this.#panels) {
      if (panel.position) this.#loadPanel(panel, this.#wrap(this.#index + panel.position));
    }
  }

  #wrap(index) {
    return (index + this.#images.length) % this.#images.length;
  }

  #loadPanel(panel, index) {
    if (panel.index === index && (panel.ready || panel.promise)) return panel.promise || Promise.resolve(true);
    panel.index = index;
    const request = panel.request = (panel.request || 0) + 1;
    panel.ready = false;
    panel.image.removeAttribute('data-ready');
    const generation = this.#generation;
    const image = this.#images[index];
    const source = new URL(image.src || image.url, window.location.href);
    const atWidth = (width) => {
      const url = new URL(source);
      url.searchParams.set('width', String(width));
      return url.href;
    };
    const element = panel.image;
    element.loading = 'eager';
    element.decoding = 'async';
    element.sizes = this.refs.primaryImage.sizes || '50vw';
    element.srcset = [240, 400, 600, 900].map((width) => atWidth(width) + ' ' + width + 'w').join(', ');
    element.src = atWidth(900);
    element.alt = image.alt || this.refs.imageLink.getAttribute('aria-label') || '';
    if (image.width) element.width = image.width;
    if (image.height) element.height = image.height;
    panel.promise = element.decode().then(() => {
      if (generation !== this.#generation || panel.request !== request || !this.isConnected || !element.naturalWidth) return false;
      panel.ready = true;
      element.setAttribute('data-ready', 'true');
      if (this.#pointer?.axis === 'x') this.#schedulePaint();
      return true;
    }).catch(() => false).finally(() => {
      if (generation === this.#generation && panel.request === request) panel.promise = null;
    });
    return panel.promise;
  }

  #schedulePaint() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.#pointer?.axis === 'x') this.#offset = this.#dragOffset(this.#pointer.dx);
      this.#paint();
    });
  }

  #dragOffset(dx) {
    const direction = dx < 0 ? this.#rtl : -this.#rtl;
    const next = this.#panels.find((panel) => panel.position === direction);
    return next?.ready ? Math.max(-this.#width, Math.min(this.#width, dx)) : Math.sign(dx) * Math.min(16, Math.abs(dx) * .18);
  }

  #transform(panel, offset) {
    return 'translate3d(calc(' + panel.position * this.#rtl * 100 + '% + ' + offset + 'px), 0, 0)';
  }

  #paint() {
    for (const panel of this.#panels) panel.image.style.transform = this.#transform(panel, this.#offset);
    const fractionalIndex = Math.max(0, Math.min(this.#images.length - 1, this.#index - this.#offset / this.#width * this.#rtl));
    if (this.refs.progressFill) this.refs.progressFill.style.transform = 'translateX(' + fractionalIndex * this.#rtl * 100 + '%)';
  }

  #updateStatus(announce = true) {
    this.dataset.currentIndex = String(this.#index);
    if (this.refs.status) this.refs.status.textContent = announce ? (this.#index + 1) + ' / ' + this.#images.length : '';
  }

  #settle(direction) {
    this.#settlement?.finish();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    const from = this.#offset;
    const to = -direction * this.#rtl * this.#width;
    const duration = this.#reducedMotion.matches ? 0 : Math.min(320, Math.max(160, 160 + Math.abs(to - from) / this.#width * 120));
    this.removeAttribute('data-dragging');
    const animations = [];
    let finished = false;
    const cleanup = () => {
      for (const animation of animations) animation.cancel();
      this.#settlement = undefined;
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      if (direction) {
        this.#index = this.#wrap(this.#index + direction);
        for (const panel of this.#panels) {
          panel.position -= direction;
          if (panel.position < -1) panel.position = 1;
          if (panel.position > 1) panel.position = -1;
          if (panel.position === 0) {
            panel.image.setAttribute('data-active', 'true');
            panel.image.removeAttribute('aria-hidden');
          } else {
            panel.image.removeAttribute('data-active');
            panel.image.setAttribute('aria-hidden', 'true');
            const target = this.#wrap(this.#index + panel.position);
            if (panel.index !== target) {
              panel.ready = false;
              panel.image.removeAttribute('data-ready');
            }
          }
        }
      }
      this.#offset = 0;
      this.#paint();
      cleanup();
      this.#updateStatus(Boolean(direction));
      this.#warmAdjacent();
    };
    this.#settlement = {
      finish,
      cancel: () => {
        finished = true;
        cleanup();
      },
    };
    if (!duration || from === to) {
      finish();
      return;
    }
    for (const panel of this.#panels) {
      animations.push(panel.image.animate(
        [{ transform: this.#transform(panel, from) }, { transform: this.#transform(panel, to) }],
        { duration, easing: 'cubic-bezier(.22, .75, .25, 1)', fill: 'forwards' }
      ));
    }
    if (this.refs.progressFill) {
      const progress = this.refs.progressFill;
      const target = direction ? this.#wrap(this.#index + direction) : this.#index;
      animations.push(progress.animate(
        [{ transform: progress.style.transform }, { transform: 'translateX(' + target * this.#rtl * 100 + '%)' }],
        { duration, easing: 'cubic-bezier(.22, .75, .25, 1)', fill: 'forwards' }
      ));
    }
    Promise.all(animations.map((animation) => animation.finished)).then(finish).catch(() => {});
  }

  async #step(direction) {
    if (!this.#mode.matches || this.#images.length < 2) return;
    this.#settlement?.finish();
    const pending = ++this.#pending;
    const panel = this.#panels.find((item) => item.position === direction);
    if (!panel) return;
    await this.#loadPanel(panel, this.#wrap(this.#index + direction));
    if (pending !== this.#pending || !this.isConnected || this.#pointer) return;
    this.#settle(panel.ready ? direction : 0);
  }

  nextImage() {
    this.#step(1);
  }

  #onPointerDown = (event) => {
    if (!this.#mode.matches || this.#images.length < 2 || !event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    this.#suppressClickUntil = 0;
    this.#pending++;
    this.#settlement?.finish();
    if (this.#offset) this.#settle(0);
    this.#width = this.getBoundingClientRect().width || 1;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, axis: null, samples: [{ x: event.clientX, time: event.timeStamp }] };
    this.#warmAdjacent();
  };

  #onMouseDown = (event) => {
    if (this.#mode.matches && this.#images.length > 1) event.stopPropagation();
  };

  #onPointerMove = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    const dx = event.clientX - pointer.x;
    const dy = Math.abs(event.clientY - pointer.y);
    if (!pointer.axis && Math.max(Math.abs(dx), dy) >= 8) {
      if (Math.abs(dx) > dy * 1.2) pointer.axis = 'x';
      else if (dy >= Math.abs(dx)) {
        this.#releasePointer();
        return;
      }
    }
    if (pointer.axis !== 'x') return;
    this.#settlement?.finish();
    event.stopPropagation();
    if (event.cancelable) event.preventDefault();
    this.#suppressClickUntil = performance.now() + 600;
    this.setAttribute('data-dragging', '');
    if (!this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
    pointer.dx = dx;
    pointer.samples.push({ x: event.clientX, time: event.timeStamp });
    while (pointer.samples.length > 2 && pointer.samples[1].time < event.timeStamp - 80) pointer.samples.shift();
    this.#schedulePaint();
  };

  #onPointerUp = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    if (pointer.axis !== 'x') {
      this.#releasePointer();
      return;
    }
    event.stopPropagation();
    const dx = event.clientX - pointer.x;
    const sample = pointer.samples.find((item) => item.time >= event.timeStamp - 80) || pointer.samples.at(-1);
    const velocity = (event.clientX - sample.x) / Math.max(1, event.timeStamp - sample.time);
    const threshold = Math.min(56, Math.max(24, this.#width * .18));
    const shouldAdvance = Math.abs(dx) >= threshold || (Math.abs(dx) >= 14 && Math.abs(velocity) >= .35 && Math.sign(velocity) === Math.sign(dx));
    const direction = dx < 0 ? this.#rtl : -this.#rtl;
    const next = this.#panels.find((panel) => panel.position === direction);
    this.#offset = this.#dragOffset(dx);
    this.#paint();
    this.#suppressClickUntil = performance.now() + 600;
    this.#releasePointer();
    if (shouldAdvance && next?.ready) this.#settle(direction);
    else if (shouldAdvance) this.#step(direction);
    else this.#settle(0);
  };

  #releasePointer() {
    const pointer = this.#pointer;
    this.#pointer = null;
    if (pointer && this.hasPointerCapture(pointer.id)) this.releasePointerCapture(pointer.id);
  }

  #cancelPointer = (event) => {
    if (!this.#pointer || (event && this.#pointer.id !== event.pointerId)) return;
    const dragged = this.#pointer.axis === 'x';
    if (dragged) this.#suppressClickUntil = performance.now() + 600;
    this.#releasePointer();
    if (dragged) this.#settle(0);
  };

  #onClick = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    this.#suppressClickUntil = 0;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #onKeyDown = (event) => {
    if (!this.#mode.matches || this.#images.length < 2 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#step((event.key === 'ArrowLeft' ? -1 : 1) * this.#rtl);
  };
}

if (!customElements.get('teylens-card-swipe-component')) {
  customElements.define('teylens-card-swipe-component', TeylensCardSwipe);
}
